import inRange from 'lodash/inRange';

import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import { roleTaxonomyDefinition } from '../definitions/roleTaxonomy';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  isSingleValueFilter,
  type SingleValueFilter,
} from '../types/filters';
import {
  describeTaxonomyRejection,
  type TaxonomyFilterRemoval,
} from './taxonomyFilterRemoval';

export const validateRoleFilters = ({
  filters,
  taxonomyVersion,
  onRemoveFilter,
}: {
  filters: [FilterId, Filter][];
  taxonomyVersion?: string;
  onRemoveFilter?: (filterId: FilterId, removal: TaxonomyFilterRemoval) => void;
}): [FilterId, Filter][] => {
  let parsedFiltersArray = filters;

  // get role taxonomy filter
  const roleTaxonomyFilterEntry = parsedFiltersArray.find(
    ([id]) => id === FilterId.ROLE_TAXONOMY
  );
  const roleTaxonomyFilter = roleTaxonomyFilterEntry?.[1];

  if (roleTaxonomyFilter && isSingleValueFilter(roleTaxonomyFilter)) {
    const value = roleTaxonomyFilter.value;
    let shouldMarkUserSubmitted = false;

    try {
      // if role taxonomy does not have a value defined
      if (!value) throw new Error('Role taxonomy value not defined');
      // if role taxonomy value is invalid
      if (!validateRoleTaxonomyFilterValue(value)) {
        throw new Error('Invalid role taxonomy filter value');
      }

      // if role taxonomy version does not match the version in the URL
      if (taxonomyVersion && value.version !== taxonomyVersion) {
        throw new Error('Role taxonomy version mismatch');
      }

      const typedValue = value as CustomRoleTaxonomySelection;
      const isDefaultSelection = !!typedValue?.default;
      if (!isDefaultSelection) {
        shouldMarkUserSubmitted = true;
      }
    } catch {
      /** If any of the above conditions fail, reset the role taxonomy filter to default
       * and clear the role filter if it has selections */
      console.error(value);

      const defaultRoleTaxonomyFilter: SingleValueFilter<CustomRoleTaxonomySelection> =
        {
          ...roleTaxonomyDefinition,
          value: { default: true, version: taxonomyVersion },
        };

      parsedFiltersArray = parsedFiltersArray.map(([id, filter]) =>
        id === FilterId.ROLE_TAXONOMY
          ? [id, defaultRoleTaxonomyFilter]
          : [id, filter]
      );

      const roleFilterEntry = parsedFiltersArray.find(
        ([id]) => id === FilterId.ROLE
      );
      const roleFilter = roleFilterEntry?.[1];

      let didRemoveRoleFilters = false;

      if (
        roleFilter &&
        roleFilter.type === FilterType.TREE &&
        roleFilter.selections?.length
      ) {
        parsedFiltersArray = parsedFiltersArray.map(([id, filter]) =>
          id === FilterId.ROLE
            ? [id, { ...filter, primarySelections: [], selections: [] }]
            : [id, filter]
        );
        didRemoveRoleFilters = true;
      }

      // Also clear MULTI_ROLE instances when taxonomy is outdated
      const multiRoleFilterEntry = parsedFiltersArray.find(
        ([id]) => id === FilterId.MULTI_ROLE
      );
      const multiRoleFilter = multiRoleFilterEntry?.[1];

      if (
        multiRoleFilter &&
        isMultiFilter(multiRoleFilter) &&
        multiRoleFilter.wrappedFilterId === FilterId.ROLE &&
        multiRoleFilter.instances?.length
      ) {
        parsedFiltersArray = parsedFiltersArray.map(([id, filter]) =>
          id === FilterId.MULTI_ROLE
            ? [id, { ...filter, instances: [] }]
            : [id, filter]
        );
        didRemoveRoleFilters = true;
      }

      // Only show toast if we actually removed role filters
      if (didRemoveRoleFilters) {
        onRemoveFilter?.(
          FilterId.ROLE,
          describeTaxonomyRejection({
            value,
            isValidValue: validateRoleTaxonomyFilterValue(value),
            currentVersion: taxonomyVersion,
          })
        );
      }
    }

    if (shouldMarkUserSubmitted) {
      parsedFiltersArray = parsedFiltersArray.map(([id, filter]) =>
        id === FilterId.ROLE_TAXONOMY
          ? [
              id,
              {
                ...filter,
                isUserSubmitted: true,
              },
            ]
          : [id, filter]
      );
    }
  }

  return parsedFiltersArray;
};

const validateRoleTaxonomyFilterValue = (
  value: unknown
): value is CustomRoleTaxonomySelection => {
  if (typeof value !== 'object' || value === null) return false;

  if ('default' in value && typeof value.default === 'boolean') {
    return true;
  }
  if (
    'ricsK10Id' in value &&
    typeof value.ricsK10Id === 'string' &&
    inRange(Number(value.ricsK10Id), 1, 11)
  ) {
    return true;
  }
  if (
    'ricsK50Id' in value &&
    typeof value.ricsK50Id === 'string' &&
    inRange(Number(value.ricsK50Id), 1, 51)
  ) {
    return true;
  }
  if (
    'taxonomyId' in value &&
    typeof value.taxonomyId === 'number' &&
    // 0-60 are the adaptive/generic cuts; 61+ are custom taxonomies. Bump the
    // bound whenever the backend advertises a higher custom id, or deeplinks
    // and saved views carrying it silently lose their role taxonomy.
    inRange(value.taxonomyId, 0, 67)
  ) {
    return true;
  }

  return false;
};
